import { REACTIONS, withSkinTone, type SkinTone } from '@live-stream/shared'

/**
 * Animated emoji, the ones Meet uses: Google's Noto Animated Emoji, served
 * as looping WebP from the Google Fonts CDN (Apache-2.0 assets, no account,
 * no cost). A 🎉 that bursts and a 👏 that claps is a picture, not a glyph —
 * no font animates — so every reaction is drawn from one of these, with the
 * text glyph underneath until the picture has loaded, and instead of it when
 * it cannot.
 *
 * The base URL is a client option (`emojiBase`), so a deployment that would
 * rather not touch a third-party CDN can point it at a copy of the same
 * files on its own host.
 */

/** Google Fonts' own copy of Noto Animated Emoji. */
export const DEFAULT_EMOJI_BASE = 'https://fonts.gstatic.com/s/e/notoemoji/latest'

/** Noto's naming: the code points of the sequence, lower-case hex, joined by underscores. */
export function emojiCodepoints(emoji: string): string {
  return Array.from(emoji)
    .map((ch) => (ch.codePointAt(0) ?? 0).toString(16))
    .join('_')
}

export function animatedEmojiUrl(emoji: string, base: string = DEFAULT_EMOJI_BASE): string {
  return `${base.replace(/\/$/, '')}/${emojiCodepoints(emoji)}/512.webp`
}

const warmed = new Set<string>()

/**
 * Fetch the pictures for the bar ahead of the tap, so the first reaction is
 * instant rather than half a second late. Once per emoji per page.
 */
export function preloadReactions(tone: SkinTone, base: string = DEFAULT_EMOJI_BASE): void {
  if (typeof Image === 'undefined') return
  for (const emoji of REACTIONS) {
    const url = animatedEmojiUrl(withSkinTone(emoji, tone), base)
    if (warmed.has(url)) continue
    warmed.add(url)
    const img = new Image()
    img.decoding = 'async'
    img.src = url
  }
}
